import React, { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import ComponentTable from '../../components/ComponentTable';
import BackButton from '../../components/BalanceComponent/BackButton';
import PromoGroupsService from './../../services/promoGroups.service';
import ModelService_v2 from './../../services/models_V2.service';
import { getPromoTags } from '../../actions/groups';
import { reloadTable } from '../../actions/AsyncTable';
import { change, formValueSelector, touch } from 'redux-form';
import { getErrorNotification } from '../../actions/toast';
import store from './../../store';

const selector = formValueSelector('from-tableComponent');

const toMark = value => (value === true || value === false ? value : null);

export default function PromoVisitsPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const saving = useRef(false);
    const sent = useRef({});
    const mounted = useRef(true);

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    useEffect(() => {
        dispatch(getPromoTags());
    }, [dispatch]);

    const keepUnsaved = failed => {
        const fresh =
            (store.getState().AsyncTable && store.getState().AsyncTable.table && store.getState().AsyncTable.table.data) ||
            [];
        failed.forEach(({ item }) => {
            const server = fresh.find(el => String(el.id) === String(item.student_id));
            ['isPromoPresent', 'tag', 'comment'].forEach(field => {
                const name = JSON.stringify({ code: field, id: item.student_id });
                const current = selector(store.getState(), name);
                const base = server ? server[field] : undefined;
                if (String(current === undefined || current === null ? '' : current) !==
                    String(base === undefined || base === null ? '' : base)) {
                    return;
                }
                store.dispatch(change('from-tableComponent', name, item[field]));
            });
        });
    };

    const callBackDataHW = async values => {
        if (saving.current) return;
        const rows = new Map();
        for (let key in values) {
            const field = JSON.parse(key);
            if (!field || field.id === undefined || field.id === null) continue;
            const rowKey = JSON.stringify(field.id);
            if (!rows.has(rowKey)) rows.set(rowKey, { student_id: field.id });
            rows.get(rowKey)[field.code] = values[key];
        }
        const loaded =
            (store.getState().AsyncTable && store.getState().AsyncTable.table && store.getState().AsyncTable.table.data) ||
            [];
        const differs = (item, source) =>
            toMark(item.isPromoPresent) !== toMark(source.isPromoPresent) ||
            (item.tag || '') !== (source.tag || '') ||
            (item.comment || '') !== (source.comment || '');
        const changed = [...rows.values()].filter(item => {
            const original = loaded.find(el => String(el.id) === String(item.student_id));
            if (!original) return false;
            const already = sent.current[item.student_id];
            if (already) return differs(item, already);
            return differs(item, original);
        });
        const missing = [...rows.values()].filter(item => !item.tag);
        if (missing.length) {
            store.dispatch(
                touch(
                    'from-tableComponent',
                    ...missing.map(item => JSON.stringify({ code: 'tag', id: item.student_id }))
                )
            );
            getErrorNotification(t('promoVisits.tagRequired'));
            return;
        }
        if (!changed.length) {
            getErrorNotification(t('promoVisits.nothingToSave'));
            return;
        }
        saving.current = true;
        const failed = [];
        try {
            for (const item of changed) {
                try {
                    await PromoGroupsService.SaveAttendance(
                        {
                            student_id: item.student_id,
                            attendance_status: item.isPromoPresent === true ? 'attended' : 'absent',
                            tag: item.tag,
                            comment: item.comment || '',
                        },
                        false,
                        { group_id: id }
                    );
                    sent.current[item.student_id] = {
                        isPromoPresent: item.isPromoPresent,
                        tag: item.tag,
                        comment: item.comment,
                    };
                } catch (error) {
                    const message = Array.isArray(error && error.message)
                        ? error.message.join('. ')
                        : (error && error.message) || '';
                    failed.push({ student_id: item.student_id, message, item });
                }
            }
            if (failed.length) {
                const lines = failed
                    .map(item => {
                        const student = loaded.find(el => String(el.id) === String(item.student_id));
                        const name = student ? `${student.last_name} ${student.first_name}` : item.student_id;
                        return item.message ? `${name} — ${item.message}` : name;
                    })
                    .join('; ');
                getErrorNotification(`${t('promoVisits.notSaved')}: ${lines}`);
            }
        } finally {
            if (mounted.current) {
                await reloadTable()(store.dispatch, store.getState);
                if (mounted.current) keepUnsaved(failed);
            }
            saving.current = false;
        }
    };

    return (
        <Fragment>
            <BackButton to={`/groups/${id}`} label={t('sidebar.groups')} />
            <h2 className="container-header">{t('promoVisits.title')}</h2>
            <ComponentTable
                source={{
                    getData: (page, size, sort, filter) => {
                        const query = { page: page || 0, size: size || 100 };
                        if (sort && sort.orderBy) {
                            query.orderBy = sort.orderBy;
                            query.order = sort.order || 'asc';
                        }
                        if (filter) query.filter = filter;
                        return PromoGroupsService.GetAttendance(false, query, { group_id: id });
                    },
                    getModel: () => ModelService_v2.PromoGroupsAttendance(),
                }}
                actions={[]}
                blockOptions={true}
                selectable={true}
                enableFilter={false}
                title={t('table.users_list')}
                toolbarvisible={true}
                typeTable="promoVisits"
                callBackDataHW={callBackDataHW}
            />
        </Fragment>
    );
}
